import React, { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Form, FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { supabase } from "@/integrations/supabase/client";
import { useNotify } from "@/hooks/useNotify";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { MoreVertical, Pencil, Trash2 } from "lucide-react";
import { formatDistance } from "date-fns";
import { useAuth } from "@/contexts/useAuth";

interface SupplierNotesProps {
  supplierId: string;
}

interface Note {
  id: string;
  supplier_id: string;
  author_id: string;
  note: string;
  created_at: string;
  updated_at: string;
}

const formSchema = z.object({
  note: z.string().min(1, "Note cannot be empty").max(1000, "Note is too long (maximum 1000 characters)"),
});
type FormData = z.infer<typeof formSchema>;

const SupplierNotes: React.FC<SupplierNotesProps> = ({ supplierId }) => {
  const { user } = useAuth();
  const [notes, setNotes] = useState<Note[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingText, setEditingText] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<Note | null>(null);
  const notify = useNotify();
  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: { note: "" },
  });

  useEffect(() => {
    void loadNotes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supplierId]);

  const loadNotes = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("supplier_notes")
      .select("*")
      .eq("supplier_id", supplierId)
      .order("created_at", { ascending: false });
    if (error) {
      notify.error(error);
    } else {
      setNotes(data || []);
    }
    setLoading(false);
  };

  const onSubmit = async (formData: FormData) => {
    if (!user) {
      notify.error("You must be logged in to add notes.");
      return;
    }
    const { error } = await supabase.from("supplier_notes").insert({
      supplier_id: supplierId,
      author_id: user.id,
      note: formData.note,
    });
    if (error) {
      notify.error(error);
      return;
    }
    notify.success("Note added");
    form.reset();
    void loadNotes();
  };

  const startEdit = (n: Note) => {
    setEditingId(n.id);
    setEditingText(n.note);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingText("");
  };

  const saveEdit = async () => {
    if (!editingId) return;
    const trimmed = editingText.trim();
    if (!trimmed) {
      notify.error("Note cannot be empty.");
      return;
    }
    setSavingEdit(true);
    const { error, data } = await supabase
      .from("supplier_notes")
      .update({ note: trimmed })
      .eq("id", editingId)
      .select()
      .maybeSingle();
    setSavingEdit(false);
    if (error || !data) {
      notify.error(error ?? new Error("Failed to update note"));
      return;
    }
    notify.success("Note updated");
    setEditingId(null);
    setEditingText("");
    void loadNotes();
  };

  const handleDelete = async () => {
    if (!pendingDelete) return;
    const { error } = await supabase.from("supplier_notes").delete().eq("id", pendingDelete.id);
    if (error) {
      notify.error(error);
      return;
    }
    notify.success("Note deleted");
    setPendingDelete(null);
    void loadNotes();
  };

  const wasEdited = (n: Note) =>
    new Date(n.updated_at).getTime() - new Date(n.created_at).getTime() > 1000;

  return (
    <div className="space-y-4">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-3">
          <FormField
            control={form.control}
            name="note"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Textarea
                    placeholder="Add a note about this supplier..."
                    className="min-h-[88px]"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <div className="flex justify-end">
            <Button type="submit" size="sm">Add note</Button>
          </div>
        </form>
      </Form>

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading notes…</p>
      ) : notes.length === 0 ? (
        <div className="py-6 text-center text-sm text-muted-foreground border border-dashed rounded-md">
          No notes yet for this supplier.
        </div>
      ) : (
        <ul className="space-y-3">
          {notes.map((n) => {
            const isEditing = editingId === n.id;
            const isOwner = user?.id === n.author_id;
            return (
              <Card key={n.id}>
                <CardHeader className="pb-2 flex flex-row items-start justify-between space-y-0">
                  <CardDescription>
                    {formatDistance(new Date(n.created_at), new Date(), { addSuffix: true })}
                    {wasEdited(n) && <span className="ml-1 italic">(edited)</span>}
                  </CardDescription>
                  {isOwner && !isEditing ? (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-7 w-7">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => startEdit(n)}>
                          <Pencil className="h-4 w-4 mr-2" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          className="text-destructive focus:text-destructive"
                          onClick={() => setPendingDelete(n)}
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  ) : null}
                </CardHeader>
                <CardContent className="pt-0">
                  {isEditing ? (
                    <div className="space-y-2">
                      <Textarea
                        value={editingText}
                        onChange={(e) => setEditingText(e.target.value)}
                        className="min-h-[88px]"
                        maxLength={1000}
                      />
                      <div className="flex justify-end gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={cancelEdit}
                          disabled={savingEdit}
                        >
                          Cancel
                        </Button>
                        <Button size="sm" onClick={saveEdit} disabled={savingEdit}>
                          Save
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm whitespace-pre-wrap">{n.note}</p>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </ul>
      )}

      <AlertDialog
        open={!!pendingDelete}
        onOpenChange={(o) => !o && setPendingDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this note?</AlertDialogTitle>
            <AlertDialogDescription>This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default SupplierNotes;
